import {Button, Card, Col, Form, Row, Table} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {RequireAuth} from "Components/Security/RequireAuth";
import {DefaultContainer} from "Views/Shared/DefaultContainer";
import {FormLabelRequiredAsterisk} from "Components/Form/FormLabelRequiredAsterisk";
import {useQuery} from "react-query";
import {useAppDispatch} from "hooks";
import {appLoading} from "Store/loadingSlice";
import {getReceipts} from "Api/Query/ReceiptQuery";
import MonthSelector from "Views/Datetime/MonthSelector";
import YearSelector from "Views/Datetime/YearSelector";
import {AllChildSelect} from "Components/Child/AllChildSelect";
import {Notificator} from "Services/Notificator/Notificator";
import {forceDownloadFromUrl} from "Services/File/FileDownloader";
import Pagination from "rc-pagination";

const PAGE_SIZE = 12

const MONTH_NAMES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

const getMonthName = (month: number): string => MONTH_NAMES_ES[month - 1] ?? ''

export const ReceiptList: React.FC = () => {

    const dispatch = useAppDispatch()
    const [page, setPage] = useState<number>(1)
    const [totalRecords, setTotalRecords] = useState<number>(0)
    const [selectedMonth, setSelectedMonth] = useState<number>(-1)
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
    const [selectedChildId, setSelectedChildId] = useState<string>('')
    const [searchFilters, setSearchFilters] = useState<{ [key: string]: any }>({
        year: new Date().getFullYear(),
        month: -1,
        size: PAGE_SIZE,
        page: 1
    })
    const [receipts, setReceipts] = useState<any[]>([])
    const [hasSearched, setHasSearched] = useState<boolean>(false)
    const [filterKey, setFilterKey] = useState<number>(0)

    const exportQuery = useQuery({
        enabled: false,
        queryKey: ['receiptsExport'],
        queryFn: () => getReceipts({...(selectedMonth !== -1 ? {month: selectedMonth} : {}), year: selectedYear, ...(selectedChildId ? {childId: selectedChildId} : {}), export: 1}),
        onSuccess: (response: any) => {
            const url = response?.data?._result?.url ?? ''
            if (url) {
                forceDownloadFromUrl(url)
            } else {
                Notificator.warning('No se encontraron datos para exportar.')
            }
        },
        onError: () => {
            Notificator.error('Se ha producido un error al exportar los recibos.')
        }
    })

    const receiptsQuery = useQuery({
        enabled: false,
        queryKey: ['receiptsList'],
        queryFn: () => getReceipts(searchFilters),
        onSuccess: (response: any) => {
            const data = response?.data?._result ?? {}
            const items = data?.items ?? data ?? []
            setReceipts(Array.isArray(items) ? items : [])
            setTotalRecords(data?.total ?? 0)
            setHasSearched(true)
        },
        onError: () => {
            Notificator.error('Se ha producido un error al obtener los recibos.')
            setHasSearched(true)
        }
    })

    const buildFilters = (pageNum: number = 1): { [key: string]: any } => {
        const filters: { [key: string]: any } = {
            year: selectedYear,
            month: selectedMonth,
            size: PAGE_SIZE,
            page: pageNum
        }
        if (selectedMonth !== -1) {
            filters.month = selectedMonth
        }
        if (selectedChildId) {
            filters.childId = selectedChildId
        }
        return filters
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setPage(1)
        setHasSearched(true)
        setSearchFilters(buildFilters(1))
    }

    const handleClearFilters = () => {
        setSelectedMonth(-1)
        setSelectedYear(new Date().getFullYear())
        setSelectedChildId('')
        setSearchFilters({
            year: new Date().getFullYear(),
            month: -1,
            size: PAGE_SIZE,
            page: 1
        })
        setPage(1)
        setReceipts([])
        setHasSearched(false)
        setTotalRecords(0)
        setFilterKey(prev => prev + 1)
    }

    const onPaginationPageChange = (selectedPage: number) => {
        setPage(selectedPage)
        setSearchFilters(buildFilters(selectedPage))
    }

    useEffect(() => {
        if (hasSearched) {
            receiptsQuery.refetch()
        }
    }, [searchFilters])

    useEffect(() => {
        dispatch(appLoading({value: receiptsQuery.isLoading, text: 'Cargando'}))
    }, [receiptsQuery.isLoading])

    useEffect(() => {
        dispatch(appLoading({value: receiptsQuery.isRefetching, text: 'Cargando'}))
    }, [receiptsQuery.isRefetching])

    useEffect(() => {
        dispatch(appLoading({value: exportQuery.isLoading || exportQuery.isRefetching, text: 'Exportando'}))
    }, [exportQuery.isLoading, exportQuery.isRefetching])

    return (
        <RequireAuth>
            <DefaultContainer>
                <Col id={"receiptList"}>
                    <Row className={"align-items-center"}>
                        <Col>
                            <h1 className={"pb-lg-4"}>Recibos</h1>
                        </Col>
                        <Col xs={"auto"}>
                            <Button
                                className={"purple-btn btn btn-primary"}
                                onClick={() => exportQuery.refetch()}
                                disabled={exportQuery.isRefetching || exportQuery.isLoading || !hasSearched || receipts.length === 0}
                            >
                                {exportQuery.isRefetching || exportQuery.isLoading ? 'Exportando ...' : 'Exportar'}
                            </Button>
                        </Col>
                    </Row>

                    <Row className={"mb-2"}>
                        <Card className={"px-0 mx-2 my-2"}>
                            <Card.Body>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col sm={3}>
                                            <Form.Group controlId={"monthFilter"}>
                                                <Form.Label>
                                                    Mes
                                                </Form.Label>
                                                <MonthSelector
                                                    key={`month-${filterKey}`}
                                                    onChange={(month: number) => setSelectedMonth(month)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={3}>
                                            <Form.Group controlId={"yearFilter"}>
                                                <Form.Label>
                                                    Año&nbsp;<FormLabelRequiredAsterisk/>
                                                </Form.Label>
                                                <YearSelector
                                                    key={`year-${filterKey}`}
                                                    onChange={(year: number) => setSelectedYear(year)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={3}>
                                            <Form.Group controlId={"childFilter"}>
                                                <Form.Label>
                                                    Niño/a
                                                </Form.Label>
                                                <AllChildSelect
                                                    key={`child-${filterKey}`}
                                                    onChange={(option: any) => setSelectedChildId(option?.value ?? '')}
                                                    value={selectedChildId}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={3} className={"d-flex flex-column"}>
                                            <div className="mt-auto d-flex gap-2">
                                                <Button
                                                    type={"submit"}
                                                    className={"purple-btn btn btn-primary"}
                                                    disabled={receiptsQuery.isRefetching || receiptsQuery.isLoading}
                                                >
                                                    {
                                                        receiptsQuery.isRefetching || receiptsQuery.isLoading
                                                            ? 'Buscando ...'
                                                            : 'Buscar'
                                                    }
                                                </Button>
                                                <Button
                                                    type={"button"}
                                                    variant={"outline-secondary"}
                                                    onClick={handleClearFilters}
                                                    disabled={receiptsQuery.isRefetching || receiptsQuery.isLoading}
                                                >
                                                    Limpiar filtros
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Row>

                    {hasSearched && (
                        <Row className={"mt-3"}>
                            <Col xs={12}>
                                {receipts.length === 0 ? (
                                    <p>No se encontraron recibos para el mes y año seleccionados.</p>
                                ) : (
                                    <>
                                        <Table striped bordered hover responsive>
                                            <thead>
                                            <tr>
                                                <th>Nombre completo</th>
                                                <th>Centro</th>
                                                <th>Mes</th>
                                                <th>Observaciones</th>
                                                <th>IBAN</th>
                                                <th>BIC</th>
                                                <th>Inscripción</th>
                                                <th>Nº Niño/a</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {receipts.map((receipt: any, index: number) => (
                                                <tr key={index}>
                                                    <td>{receipt?.child?.fullname ?? ''}</td>
                                                    <td>{receipt?.center?.name ?? ''}</td>
                                                    <td>{receipt?.month ? getMonthName(receipt.month) : ''}</td>
                                                    <td>{receipt?.bank?.concept ?? ''}</td>
                                                    <td>{receipt?.bank?.iban ?? ''}</td>
                                                    <td>{receipt?.bank?.bic ?? ''}</td>
                                                    <td>{receipt?.inscription?.id ?? ''}</td>
                                                    <td>{receipt?.child?.number ?? ''}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                        {totalRecords > PAGE_SIZE &&
                                            <Pagination
                                                total={totalRecords}
                                                pageSize={PAGE_SIZE}
                                                current={page}
                                                onChange={onPaginationPageChange}
                                            />
                                        }
                                    </>
                                )}
                            </Col>
                        </Row>
                    )}
                </Col>
            </DefaultContainer>
        </RequireAuth>
    )
}
